import { Business as BusinessIcon } from '@mui/icons-material';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Character, Organization } from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import {
  EntityVisual,
  SectionDisplay,
  SectionOrganizationMain
} from '@/components/entity-display';
import { EntityTimelineStepper } from '@/components/entity-timeline';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { OrganizationMembersGraph } from '@/components/organization-members-graph';
import { Page } from '@/components/page';
import { useUserSession } from '@/context/user-session-context';
import { useCharacterAccessLayer } from '@/features/characters';
import { useOrganizationAccessLayer } from '@/features/organizations';
import {
  removeOrganizationFromCharacters,
  removeOrganizationFromOrganizations,
} from '@/features/organizations/utils/membership-sync';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export const Route = createFileRoute('/organizations/view/$id')({
  component: OrganizationViewPage,
});

function ViewOrganizationPage() {
  const navigate = useNavigate();
  const { id: organizationId } = Route.useParams();
  const { novel, updateEntityItem, deleteItem, entityDao } = useNovelRepository();
  const { t } = useTranslation();
  const { hasFeature } = useUserSession();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);


  const [timelineEntityData, setTimelineEntityData] = useState<Record<string, unknown> | null>(null);

  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    organization: organizationData,
    organizations: organizationSummaries,
    sections: customFieldSections,
    isLoading,
    deleteOrganization,
  } = useOrganizationAccessLayer(organizationId);

  const { characters: characterSummaries } = useCharacterAccessLayer();
  const isLoadingHierarchyRecords = false;

  // Handle timeline data changes
  const handleTimelineDataChange = useCallback((data: Record<string, unknown>) => {
    setTimelineEntityData(data);
  }, []);

  // Current display data (timeline or base)
  const displayData = (timelineEntityData || organizationData || {}) as Organization;

  const handleDelete = async () => {
    if (!organizationId) return;
    setIsDeleting(true);
    try {
      if (isOrganizationHierarchyEnabled && novel && entityDao) {
        // Find affected characters
        const charIds = organizationData?.organizationMembers?.map(m => m.characterId) || [];
        const characterDocs = (await Promise.all(
          charIds.map(id => entityDao.getItem<Character>(novel.ownerId, novel, 'entities', 'characters', id))
        )).filter(Boolean) as Character[];

        // Find affected organizations
        const orgIds = [
          ...(organizationData?.parentOrganizationIds || []),
          ...(organizationData?.childOrganizationIds || [])
        ];
        const organizationDocs = (await Promise.all(
          orgIds.map(id => entityDao.getItem<Organization>(novel.ownerId, novel, 'entities', 'organizations', id))
        )).filter(Boolean) as Organization[];

        await removeOrganizationFromCharacters({
          organizationId,
          characters: characterDocs,
          updateEntityItem,
          organizationMembers: organizationData?.organizationMembers || [],
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
        await removeOrganizationFromOrganizations({
          organizationId,
          organizations: organizationDocs,
          updateEntityItem,
          parentOrganizationIds: organizationData?.parentOrganizationIds || [],
          childOrganizationIds: organizationData?.childOrganizationIds || [],
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }
      await deleteOrganization(organizationId);
      toast({
        title: t('entity_form.organizations.delete_success_title'),
        description: t('entity_form.organizations.delete_success_desc'),
      });
      navigate({ to: '/organizations' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  // Loading or waiting state
  if (isLoading || (!organizationData && !novel.id)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not Found
  if (!organizationData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="text.secondary">{t('entity_view.organizations.not_found_title')}</Typography>
        <Typography variant="body1">{t('entity_view.organizations.not_found_desc')}</Typography>
        <Button onClick={() => navigate({ to: '/organizations' })} sx={{ mt: 2 }}>{t('entity_view.organizations.back')}</Button>
      </Box>
    );
  }

  return (
    <Page
      title={organizationData.name || t('entity_view.organizations.view_fallback')}
      breadcrumbs={[
        { href: '/organizations', label: t('entity_list.organizations.title') },
        { label: organizationData.name || t('entity_view.organizations.view_fallback') }
      ]}
      layoutMode="scrolling"
      actions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting || isLoadingHierarchyRecords}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_form.common.delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate({ to: '/organizations/edit/$id', params: { id: organizationId } })}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_view.organizations.edit')}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={4}>
        {/* Main Content Area (70%) */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={4}>
            {/* Basic Identity Section */}
            <SectionOrganizationMain data={displayData} />
            {isOrganizationHierarchyEnabled && (
              <OrganizationMembersGraph
                key={organizationId}
                organization={displayData}
                characters={characterSummaries}
                organizations={organizationSummaries}
                novelId={novel?.id}
                novelTheme={novel?.theme}
                isLoading={isLoadingHierarchyRecords}
                onMemberClick={(characterId) => navigate({ to: '/characters/view/$id', params: { id: characterId } })}
                onOrganizationClick={(memberOrganizationId) => navigate({ to: '/organizations/view/$id', params: { id: memberOrganizationId } })}
              />
            )}

            {/* Timeline Section */}
            {novel && organizationId && organizationData && (
              <EntityTimelineStepper
                currentEntityData={organizationData}
                onEntityDataChange={handleTimelineDataChange}
              />
            )}

            {/* Custom Fields Sections */}
            {customFieldSections.map((section) => (
              <SectionDisplay
                key={section.id}
                title={section.title}
                fields={section.fields}
                data={displayData}
              />
            ))}
          </Stack>
        </Grid>

        {/* Sidebar Area (30%) */}
        <EntityVisual
          title={t('entity_view.organizations.visual')}
          images={displayData.images}
          primaryImageId={displayData.primaryImageId}
          altText={organizationData.name || 'Organization'}
          placeholderIcon={<BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />}
        />
      </Grid>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemName={organizationData?.name}
        description={t('entity_form.organizations.delete_desc')}
      />
    </Page>
  );
}

function OrganizationViewPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
        <ViewOrganizationPage />
      </Suspense>
    </AuthGuard>
  );
}
